import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError, tokenStore } from "../lib/api";
import { Header } from "../components/Header";
import { SmartMap } from "../components/SmartMap";
import { reverseGeocode } from "../components/PlaceAutocomplete";
import { RatingDialog } from "../components/RatingDialog";
import { useRealtime } from "../hooks/useRealtime";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CreditCard, Banknote, Zap, Briefcase, Dog, Plane, Repeat, Moon, Calendar, Clock, LocateFixed, Wand2, Loader2, Phone } from "lucide-react";
import { RideChatDialog } from "../components/RideChatDialog";
import {
  LocationSelect,
  Stat,
  StopsEditor,
  ToggleRow,
} from "../components/passenger/BookingFormHelpers";
import { FareBreakdown } from "../components/passenger/FareBreakdown";
import { RideStatusPanel } from "../components/passenger/RideStatusPanel";
import { ActiveRideDetail } from "../components/passenger/ActiveRideDetail";
import { ScheduledRideCard } from "../components/passenger/ScheduledRideCard";
import { HistoryItem } from "../components/passenger/HistoryItem";
import { PassengerAiBooking } from "../components/passenger/PassengerAiBooking";
import { CompletionSummaryDialog } from "../components/passenger/CompletionSummaryDialog";
import { useCompletionRecovery } from "../hooks/useCompletionRecovery";
import { defaultScheduleValue, formatLondonLocal, londonLocalToDate, scheduleProblem, SCHEDULE_MAX_AHEAD_MS } from "../lib/londonTime";

export const PRESET_LOCATIONS = [
  { name: "Basingstoke Station", lat: 51.2683, lng: -1.0884 },
  { name: "Festival Place", lat: 51.2664, lng: -1.0879 },
  { name: "Basingstoke Hospital", lat: 51.2873, lng: -1.0958 },
  { name: "Chineham Centre", lat: 51.2941, lng: -1.0524 },
  { name: "Reading Station", lat: 51.4585, lng: -0.9710 },
  { name: "Heathrow Airport", lat: 51.4700, lng: -0.4543 },
  { name: "Southampton Airport", lat: 50.9503, lng: -1.3568 },
  { name: "Winchester", lat: 51.0632, lng: -1.3080 },
];

export default function Passenger() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [pickup, setPickup] = useState(null);
  const [bookMode, setBookMode] = useState("quick"); // "quick" | "ai"
  const [bookingLine, setBookingLine] = useState("");
  const [dropoff, setDropoff] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [vehicleType, setVehicleType] = useState("car");
  const [passengerCount, setPassengerCount] = useState(1);
  const [extras, setExtras] = useState({ has_dog: false, terminal_dropoff: false, wait_and_return: false, driver_notes: "" });
  // Multi-stop journey waypoints (#8) — ordered {name,lat,lng} | null
  // entries between pickup and drop-off, capped at 3. Held separate from
  // `extras` because each stop is a geocoded address, not a flat toggle.
  const [stops, setStops] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [activeRide, setActiveRide] = useState(null);
  const [dispatchStatus, setDispatchStatus] = useState(null);
  const [rideHistory, setRideHistory] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rideToRate, setRideToRate] = useState(null);
  const [pickMode, setPickMode] = useState(null); // null | 'pickup' | 'dropoff'
  const [myLocation, setMyLocation] = useState(null); // {lat,lng} once geolocation granted
  const [locating, setLocating] = useState(false);
  const geoInFlightRef = useRef(false); // guards against concurrent getCurrentPosition calls
  const [scheduleMode, setScheduleMode] = useState("now"); // 'now' | 'later'
  const [scheduledFor, setScheduledFor] = useState(""); // datetime-local string (YYYY-MM-DDTHH:mm)
  const [scheduledRides, setScheduledRides] = useState([]);
  const pollRef = useRef(null);
  const prevActiveRef = useRef(null);
  const [pollFailures, setPollFailures] = useState(0);
  const { completedRide, checkCompletion, dismissCompletion } = useCompletionRecovery();

  // Ride-scoped chat state (Phase 1 templated messages). `chatOpen` is
  // controlled UI; `incomingMessage` is bumped on every WS
  // `ride_message` event so RideChatDialog re-fetches when open.
  const [chatOpen, setChatOpen] = useState(false);
  const [incomingMessage, setIncomingMessage] = useState(null);

  const isAccount = !!user?.is_account_customer;

  // WebSocket push
  useRealtime(tokenStore.get(), (msg) => {
    if (["ride_completed", "ride_paid"].includes(msg.type)) checkCompletion();
    if (["ride_accepted", "ride_started", "ride_completed", "ride_cancelled", "driver_location", "driver_arrived", "driver_nearby"].includes(msg.type)) {
      fetchActive();
      if (msg.type === "driver_arrived") {
        toast.success("Your driver has arrived — please head outside.", { duration: 8000 });
        if (navigator.vibrate) navigator.vibrate([220, 110, 220]);
      } else if (msg.type === "driver_nearby") {
        toast.info("Your driver is ~2 minutes away — get ready.", { duration: 6000 });
        if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
      }
    }
    // Ride-scoped templated message from the driver. Surface as a toast
    // and bump `incomingMessage` so an open RideChatDialog refreshes.
    if (msg.type === "ride_message") {
      const isSelf = msg.from_user_id && user && msg.from_user_id === user.id;
      if (!isSelf) {
        toast.message(`${msg.from_name || "Driver"}: ${msg.body}`, {
          description: "Tap MESSAGE on the ride card to reply.",
        });
      }
      setIncomingMessage(msg);
    }
    // Stripe webhook → backend → WS push. When the payment lands, the
    // backend has already flipped the ride document; refresh active so
    // the "Pay £X with card" CTA disappears and the booking form returns.
    if (msg.type === "ride_payment_received") {
      toast.success(
        msg.fare ? `Payment confirmed · £${msg.fare}` : "Payment confirmed",
      );
      fetchActive();
      fetchHistory();
    }
  }, () => { fetchActive(); checkCompletion(); });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
    } else if (user.role !== "passenger") {
      navigate(user.role === "admin" ? "/admin" : "/driver", { replace: true });
    }
  }, [user, loading, navigate]);

  // Tear down chat state whenever the active ride identity changes
  // (completed, cancelled, swapped). The backend already returns an
  // empty thread for non-active rides — this just drops any stale
  // dialog/toast cadence on the client.
  useEffect(() => {
    setChatOpen(false);
    setIncomingMessage(null);
  }, [activeRide?.id]);

  // Public booking line for the "Call AI Booking" option (third channel onto
  // the same engine). Read from branding so the number is never hardcoded.
  useEffect(() => {
    api.get("/public/branding")
      .then(({ data }) => setBookingLine(data?.booking_line || ""))
      .catch(() => {});
  }, []);

  // Reset the booking form to a clean slate when an active ride ENDS
  // (completed/cancelled), so the next booking doesn't carry over the
  // previous pickup/destination/extras. Only fires on the active→none
  // transition — active-ride and scheduled-booking flows are untouched
  // (those don't render the picker). Initial mount never triggers it
  // because the previous id starts null.
  const prevActiveIdRef = useRef(null);
  useEffect(() => {
    const prevId = prevActiveIdRef.current;
    const currId = activeRide?.id || null;
    if (prevId && !currId) {
      setPickup(null);
      setDropoff(null);
      setExtras({ has_dog: false, terminal_dropoff: false, wait_and_return: false, driver_notes: "" });
      setStops([]);
      setEstimate(null);
    }
    prevActiveIdRef.current = currId;
  }, [activeRide?.id]);

  // Map the local {name,lat,lng} stop objects into the backend's
  // {address,lat,lng} waypoint shape, dropping any unfilled rows. Used
  // by both the live estimate and the ride-create call so they stay in
  // lockstep. extra_stops is derived server-side from this list.
  const validStops = () =>
    stops
      .filter((s) => s && s.lat != null && s.lng != null)
      .map((s) => ({ address: s.name, lat: s.lat, lng: s.lng }));

  useEffect(() => {
    if (!user || activeRide) return;
    // Fields start empty; only quote once BOTH locations are chosen.
    if (!pickup || !dropoff) {
      setEstimate(null);
      setQuoteLoading(false);
      return;
    }
    let ignore = false;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/passenger/estimate", {
          pickup_address: pickup.name, pickup_lat: pickup.lat, pickup_lng: pickup.lng,
          dropoff_address: dropoff.name, dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
          vehicle_type: vehicleType,
          stops: validStops(),
          ...extras,
        });
        if (!ignore) setEstimate(data);
      } catch (e) {
        // Estimate is a passive UI hint — log so we can see infra issues
        // in DevTools but never disrupt the booking flow.
        // eslint-disable-next-line no-console
        console.warn("[passenger] /passenger/estimate failed:", e?.message);
      } finally {
        if (!ignore) setQuoteLoading(false);
      }
    }, 300);
    return () => { ignore = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, vehicleType, extras, stops, user, activeRide]);

  const fetchActive = async () => {
    try {
      const { data } = await api.get("/passenger/active-ride");
      if (prevActiveRef.current && !data) checkCompletion();
      prevActiveRef.current = data;
      setActiveRide(data);
      setPollFailures(0);
    } catch (e) {
      setPollFailures((n) => n + 1);
      // eslint-disable-next-line no-console
      console.warn("[passenger] /passenger/active-ride failed:", e?.message);
    }
  };
  const fetchDispatchStatus = async () => {
    try {
      const { data } = await api.get("/passenger/dispatch-status");
      setDispatchStatus(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[passenger] /passenger/dispatch-status failed:", e?.message);
    }
  };
  const fetchHistory = async () => {
    try {
      const { data } = await api.get("/passenger/my-rides");
      setRideHistory(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[passenger] /passenger/my-rides failed:", e?.message);
    }
  };
  const fetchScheduled = async () => {
    try {
      const { data } = await api.get("/passenger/scheduled-rides");
      setScheduledRides(data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[passenger] /passenger/scheduled-rides failed:", e?.message);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchActive();
    fetchHistory();
    fetchScheduled();
    checkCompletion();
    pollRef.current = setInterval(() => { fetchActive(); fetchScheduled(); }, 5000);
    const resync = () => {
      if (document.visibilityState === "hidden") return;
      fetchActive();
      checkCompletion();
    };
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("online", resync);
    return () => {
      clearInterval(pollRef.current);
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("online", resync);
    };
  }, [user]);

  // While the ride is in the pre-acceptance phase, poll a lightweight
  // dispatch-status endpoint so we can show "No drivers online" vs
  // "Finding a driver" vs "Offered to driver" without ambiguity.
  useEffect(() => {
    const inFunnel = activeRide && ["searching", "offered"].includes(activeRide.status);
    if (!inFunnel) {
      setDispatchStatus(null);
      return;
    }
    fetchDispatchStatus();
    const t = setInterval(fetchDispatchStatus, 3500);
    return () => clearInterval(t);
    // SAFE: deps already capture the only fields read from `activeRide`
    // (.id and .status). Adding the whole `activeRide` object would
    // re-arm the 3.5s interval on every coordinate/ETA push the
    // backend sends — making the passenger's "searching for driver"
    // UI flicker. Same pattern as the Driver.jsx polling effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRide?.id, activeRide?.status]);

  // Stripe return handling
  useEffect(() => {
    const sessionId = params.get("session_id");
    const ridePaid = params.get("ride_paid");
    if (sessionId && ridePaid) {
      pollPaymentStatus(sessionId, 0);
      // clear url
      const newParams = new URLSearchParams(params);
      newParams.delete("session_id");
      newParams.delete("ride_paid");
      setParams(newParams, { replace: true });
    }
    const cancel = params.get("ride_cancel");
    if (cancel) {
      toast.info("Payment cancelled");
      const newParams = new URLSearchParams(params);
      newParams.delete("ride_cancel");
      setParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 5) {
      toast.error("Payment status check timed out");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        toast.success(`Payment of £${data.amount} confirmed`);
        // Show rating dialog
        const rides = (await api.get("/passenger/my-rides")).data;
        const justPaid = rides.find((r) => r.id === data.ride_id);
        if (justPaid) {
          setRideToRate(justPaid);
          setShowRating(true);
        }
        fetchHistory();
        fetchActive();
        return;
      }
      if (data.status === "expired") {
        toast.error("Payment session expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const scheduleIssue = scheduleMode === "later" ? scheduleProblem(scheduledFor) : null;
  const bookingIssues = [
    !pickup && { id: "pickup", text: "Select your pickup from the suggestions list" },
    !dropoff && { id: "dropoff", text: "Select your drop-off from the suggestions list" },
    scheduleIssue && { id: "schedule", text: scheduleIssue },
  ].filter(Boolean);

  const requestRide = async () => {
    // Fields open empty — require an explicit pickup + drop-off choice.
    if (!pickup || !dropoff) {
      toast.error("Please choose a pickup and drop-off location");
      return;
    }
    // Client-side check of the London wall-clock schedule; backend re-validates.
    let scheduledIso = null;
    if (scheduleMode === "later") {
      const problem = scheduleProblem(scheduledFor);
      if (problem) {
        toast.error(problem);
        return;
      }
      scheduledIso = londonLocalToDate(scheduledFor).toISOString();
    }

    setRequesting(true);
    try {
      await api.post("/passenger/request-ride", {
        pickup_address: pickup.name, pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_address: dropoff.name, dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        payment_method: paymentMethod,
        vehicle_type: vehicleType,
        passenger_count: passengerCount,
        stops: validStops(),
        ...extras,
        ...(scheduledIso ? { scheduled_for: scheduledIso } : {}),
      });
      if (scheduledIso) {
        toast.success("Ride scheduled — we'll dispatch a driver 15 min before pickup");
        setScheduleMode("now");
        setScheduledFor("");
        fetchScheduled();
      } else {
        toast.success("Searching for nearby drivers…");
        fetchActive();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRequesting(false);
    }
  };

  const cancelScheduledRide = async (rideId) => {
    if (!window.confirm("Cancel this scheduled ride?")) return;
    try {
      await api.post(`/passenger/cancel/${rideId}`);
      toast.success("Scheduled ride cancelled");
      fetchScheduled();
      fetchHistory();  // keep the Recent Trips status pill in sync
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelRide = async () => {
    if (!activeRide) return;
    try {
      await api.post(`/passenger/cancel/${activeRide.id}`);
      toast.success("Ride cancelled");
      setActiveRide(null);
      fetchHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Trigger Stripe checkout for a completed card ride.
  // Handles the "ride already paid" reconciliation case: when the backend
  // detects via Stripe lookup that the ride is actually paid (webhook may
  // have failed to land), it returns 409. We refresh active+history so the
  // Pay CTA disappears and the user sees the receipt — not a toast.
  const payWithCard = async (rideId) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        ride_id: rideId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 409 && code === "ride_already_paid") {
        toast.success("This ride is already paid — refreshing.");
        fetchActive();
        fetchHistory();
        setPaying(false);
        return;
      }
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  // Detect a freshly-completed cash ride for rating
  useEffect(() => {
    if (activeRide && activeRide.status === "completed" && activeRide.payment_method === "cash" && activeRide.payment_status === "paid") {
      setRideToRate(activeRide);
      setShowRating(true);
      // active ride becomes history now
      setActiveRide(null);
      fetchHistory();
    }
    if (activeRide && activeRide.status === "completed" && activeRide.payment_method === "card" && activeRide.payment_status !== "paid") {
      // Auto-launch payment
      // (Don't auto-redirect; offer button)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRide?.status, activeRide?.payment_status]);

  const handleMapClick = async (lat, lng) => {
    if (!pickMode || activeRide) return;
    toast.loading("Resolving address…", { id: "geo" });
    const place = await reverseGeocode(lat, lng);
    toast.success(`${pickMode === "pickup" ? "Pickup" : "Drop-off"} set: ${place.name}`, { id: "geo" });
    if (pickMode === "pickup") setPickup(place);
    else setDropoff(place);
    setPickMode(null);
  };

  // --- Current-location helpers (customer-experience slice, frontend only) ---
  // One-shot GPS fix. Degrades gracefully on denied/unsupported — the map and
  // manual address search keep working regardless.
  const requestMyLocation = (announce = false) =>
    new Promise((resolve) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        if (announce) toast.message("Location isn't available on this device — tap the map or search to set pickup.");
        resolve(null);
        return;
      }
      // Prevent overlapping requests — the silent background fetch (Pick on Map)
      // and an explicit "Use my location" tap can otherwise race each other.
      if (geoInFlightRef.current) {
        resolve(myLocation);
        return;
      }
      geoInFlightRef.current = true;
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          geoInFlightRef.current = false;
          setLocating(false);
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMyLocation(loc);
          resolve(loc);
        },
        (err) => {
          geoInFlightRef.current = false;
          setLocating(false);
          // Only surface a toast for an explicit user action (announce=true).
          // The silent background fetch triggered by engaging "Pick on Map"
          // must fail quietly — otherwise a timeout/transient error wrongly
          // tells the user their permission is off even when it's granted.
          if (announce) {
            toast.message(
              err && err.code === 1
                ? "Location permission is off — tap the map or search to set pickup."
                : "Couldn't get your location — tap the map or search to set pickup."
            );
          }
          resolve(null);
        },
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 8000 }
      );
    });

  // Reverse-geocode the current GPS fix and set it as the pickup — reuses the
  // exact same reverseGeocode + setPickup path as a manual map tap. One tap:
  // fetches the fix first if we don't have it yet, then sets pickup.
  const useMyLocationAsPickup = async () => {
    if (activeRide) return;
    let loc = myLocation;
    if (!loc) loc = await requestMyLocation(true);
    if (!loc) return; // denied / unsupported — neutral message already shown
    toast.loading("Resolving your location…", { id: "geo" });
    const place = await reverseGeocode(loc.lat, loc.lng);
    toast.success(`Pickup set: ${place.name}`, { id: "geo" });
    setPickup(place);
    setPickMode(null);
  };

  // Ask for a GPS fix the moment the map picker is engaged.
  useEffect(() => {
    if (pickMode && !myLocation && !activeRide) requestMyLocation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickMode]);

  const markers = activeRide
    ? [
        { id: "p", lat: activeRide.pickup_lat, lng: activeRide.pickup_lng, type: "pickup", label: "PICKUP" },
        ...((activeRide.stops || [])
          .filter((s) => s && s.lat != null && s.lng != null)
          .map((s, i) => ({ id: `s${i}`, lat: s.lat, lng: s.lng, type: "stop", label: `STOP ${i + 1}` }))),
        { id: "d", lat: activeRide.dropoff_lat, lng: activeRide.dropoff_lng, type: "dropoff", label: "DROPOFF" },
        ...(activeRide.driver && activeRide.driver.current_lat
          ? [{ id: "drv", lat: activeRide.driver.current_lat, lng: activeRide.driver.current_lng, type: "driver", label: activeRide.driver.vehicle_plate }]
          : []),
      ]
    : [
        ...(myLocation ? [{ id: "me", lat: myLocation.lat, lng: myLocation.lng, type: "me" }] : []),
        ...(pickup ? [{ id: "p", lat: pickup.lat, lng: pickup.lng, type: "pickup", label: "PICKUP" }] : []),
        ...(validStops().map((s, i) => ({ id: `s${i}`, lat: s.lat, lng: s.lng, type: "stop", label: `STOP ${i + 1}` }))),
        ...(dropoff ? [{ id: "d", lat: dropoff.lat, lng: dropoff.lng, type: "dropoff", label: "DROPOFF" }] : []),
      ];

  return (
    <div className="min-h-screen bg-background">
      <Header title="PASSENGER" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border-b border-border">
        <div className="lg:col-span-7 bg-white relative" style={{ minHeight: "60vh" }} data-testid="passenger-map-section">
          <SmartMap markers={markers} className="absolute inset-0" onMapClick={handleMapClick} />
          {pickMode && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-[#0A0A0A] text-white px-4 py-2 rounded-sm shadow-xl z-20 flex items-center gap-3" data-testid="pick-mode-banner">
              <span className="label-mini">CLICK ON MAP TO SET {pickMode.toUpperCase()}</span>
              <button onClick={() => setPickMode(null)} className="label-mini text-[#FFC107] hover:underline" data-testid="pick-mode-cancel">CANCEL</button>
            </div>
          )}
          {activeRide && (
            <div className="absolute top-4 left-4 right-4 lg:right-auto bg-white border border-border p-4 shadow-lg z-10 max-w-md">
              {pollFailures >= 3 && (
                <div className="mb-3 border border-[#F59E0B] bg-[#FFFBEB] text-[#92400E] text-xs font-semibold px-3 py-2" data-testid="trip-connection-warning">
                  Connection lost — reconnecting. Trip status below may be out of date.
                </div>
              )}
              <RideStatusPanel ride={activeRide} dispatchStatus={dispatchStatus} onCancel={cancelRide} onMessage={() => setChatOpen(true)} />
            </div>
          )}
        </div>

        <div className="lg:col-span-5 bg-white p-6 md:p-8 space-y-5" data-testid="passenger-booking-panel">
          {!activeRide ? (
            <>
              <div>
                <div className="label-mini text-muted-foreground">REQUEST A RIDE</div>
                <h2 className="font-heading text-3xl font-black tracking-tight mt-1">Where from &amp; to?</h2>
              </div>

              {/* Quick book | AI Assistant — AI is another channel onto the same
                  estimator + /passenger/request-ride flow. */}
              <div className="grid grid-cols-2 gap-1 border border-border" data-testid="passenger-book-mode-toggle">
                <button type="button" onClick={() => setBookMode("quick")}
                  className={`text-sm font-bold px-3 py-2.5 transition-colors ${bookMode === "quick" ? "bg-[#0A0A0A] text-white" : "hover:bg-secondary"}`}
                  data-testid="book-mode-quick">
                  Quick book
                </button>
                <button type="button" onClick={() => setBookMode("ai")}
                  className={`text-sm font-bold px-3 py-2.5 flex items-center justify-center gap-1.5 transition-colors ${bookMode === "ai" ? "bg-[#002FA7] text-white" : "hover:bg-secondary"}`}
                  data-testid="book-mode-ai">
                  <Wand2 className="w-3.5 h-3.5" /> AI Assistant
                </button>
              </div>

              {/* Call AI Booking — third channel onto the SAME engine. Dials the
                  public booking line (AI-first Voice Booking) via tel:. Shown
                  only when a booking line is configured; best on mobile. */}
              {bookingLine && (
                <a
                  href={`tel:${bookingLine}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 border border-[#002FA7]/40 bg-[#EFF3FF] hover:bg-[#E0E8FF] transition-colors group"
                  data-testid="call-ai-booking-btn"
                >
                  <span className="flex items-center gap-2.5">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#002FA7] text-white">
                      <Phone className="w-4 h-4" />
                    </span>
                    <span className="text-left">
                      <span className="block text-sm font-bold text-[#0A0A0A]">Call AI Booking</span>
                      <span className="block text-xs text-muted-foreground">Speak to our AI by phone</span>
                    </span>
                  </span>
                  <span className="text-xs font-bold text-[#002FA7] tabular-nums group-hover:translate-x-0.5 transition-transform">
                    {bookingLine}
                  </span>
                </a>
              )}

              {bookMode === "ai" && (
                <PassengerAiBooking
                  user={user}
                  onBooked={({ scheduled }) => { if (scheduled) { fetchScheduled(); } else { fetchActive(); } }}
                />
              )}

              {bookMode === "quick" && (<>
              <button
                type="button"
                onClick={useMyLocationAsPickup}
                disabled={locating}
                className="w-full flex items-center justify-center gap-2 py-3 border border-[#002FA7] text-[#002FA7] hover:bg-[#EFF3FF] disabled:opacity-50 transition-colors label-mini"
                data-testid="use-my-location-btn"
              >
                <LocateFixed className={`w-4 h-4 ${locating ? "animate-pulse" : ""}`} />
                {locating ? "LOCATING…" : "USE MY CURRENT LOCATION"}
              </button>

              <LocationSelect label="PICKUP" value={pickup} onChange={setPickup} color="#10B981" testId="pickup" onPickOnMap={() => setPickMode("pickup")} />
              <LocationSelect label="DROP-OFF" value={dropoff} onChange={setDropoff} color="#EF4444" testId="dropoff" onPickOnMap={() => setPickMode("dropoff")} />

              <div className="space-y-2">
                <div className="label-mini text-muted-foreground">VEHICLE</div>
                <div className="grid grid-cols-2 gap-px bg-border border border-border">
                  <button type="button" onClick={() => setVehicleType("car")}
                    className={`py-3 flex items-center justify-center gap-2 label-mini transition-colors ${vehicleType === "car" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
                    data-testid="vehicle-car-btn">
                    CAR (4 seats)
                  </button>
                  <button type="button" onClick={() => setVehicleType("bus")}
                    className={`py-3 flex items-center justify-center gap-2 label-mini transition-colors ${vehicleType === "bus" ? "bg-[#FFC107] text-black" : "bg-white hover:bg-secondary"}`}
                    data-testid="vehicle-bus-btn">
                    BUS (8 seats)
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="label-mini text-muted-foreground">PASSENGERS</div>
                <div className="flex items-center border border-border w-fit" data-testid="passenger-count-stepper">
                  <button type="button" onClick={() => setPassengerCount((n) => Math.max(1, n - 1))}
                    className="px-4 py-2.5 hover:bg-secondary disabled:opacity-40 label-mini transition-colors" disabled={passengerCount <= 1}
                    data-testid="passenger-count-decrement" aria-label="Fewer passengers">−</button>
                  <span className="px-5 py-2.5 font-heading font-black text-base min-w-[3.5rem] text-center border-x border-border" data-testid="passenger-count-value">{passengerCount}</span>
                  <button type="button" onClick={() => setPassengerCount((n) => Math.min(8, n + 1))}
                    className="px-4 py-2.5 hover:bg-secondary disabled:opacity-40 label-mini transition-colors" disabled={passengerCount >= 8}
                    data-testid="passenger-count-increment" aria-label="More passengers">+</button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="label-mini text-muted-foreground">PAYMENT</div>
                <div className={`grid gap-px bg-border border border-border ${isAccount ? "grid-cols-3" : "grid-cols-2"}`}>
                  <button type="button" onClick={() => setPaymentMethod("cash")}
                    className={`py-3 flex items-center justify-center gap-2 label-mini transition-colors ${paymentMethod === "cash" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
                    data-testid="pay-cash-btn">
                    <Banknote className="w-4 h-4" /> CASH
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("card")}
                    className={`py-3 flex items-center justify-center gap-2 label-mini transition-colors ${paymentMethod === "card" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
                    data-testid="pay-card-btn">
                    <CreditCard className="w-4 h-4" /> CARD
                  </button>
                  {isAccount && (
                    <button type="button" onClick={() => setPaymentMethod("account")}
                      className={`py-3 flex items-center justify-center gap-2 label-mini transition-colors ${paymentMethod === "account" ? "bg-[#002FA7] text-white" : "bg-white hover:bg-secondary"}`}
                      data-testid="pay-account-btn">
                      <Briefcase className="w-4 h-4" /> ACCOUNT
                    </button>
                  )}
                </div>
                {paymentMethod === "account" && (
                  <div className="text-xs text-[#002FA7] flex items-center gap-1.5" data-testid="account-pay-hint">
                    <Briefcase className="w-3 h-3" />
                    Billed monthly to <span className="font-bold">{user?.account_company || "your account"}</span> — no card needed.
                  </div>
                )}
              </div>

              {/* EXTRAS */}
              <div className="space-y-2" data-testid="extras-panel">
                <div className="label-mini text-muted-foreground">EXTRAS (OPTIONAL)</div>
                <div className="border border-border divide-y divide-border">
                  <StopsEditor stops={stops} onChange={setStops} max={3} />
                  <ToggleRow
                    icon={<Dog className="w-4 h-4" />}
                    label="Travelling with a dog"
                    sub="+£3 flat"
                    checked={extras.has_dog}
                    onChange={(v) => setExtras((s) => ({ ...s, has_dog: v }))}
                    testId="extra-dog"
                  />
                  <ToggleRow
                    icon={<Plane className="w-4 h-4" />}
                    label="Airport terminal drop-off"
                    sub="+£10 (covers terminal access charge)"
                    checked={extras.terminal_dropoff}
                    onChange={(v) => setExtras((s) => ({ ...s, terminal_dropoff: v }))}
                    testId="extra-terminal"
                  />
                  <ToggleRow
                    icon={<Repeat className="w-4 h-4" />}
                    label="Wait & return"
                    sub="Min fare £9 applies"
                    checked={extras.wait_and_return}
                    onChange={(v) => setExtras((s) => ({ ...s, wait_and_return: v }))}
                    testId="extra-waitreturn"
                  />
                </div>
                {/* Free-text notes for the driver — multi-stop journeys, special
                    requests, accessibility info, etc. Capped at 500 chars to
                    keep the offer card readable. Not used by fare engine. */}
                <div className="space-y-1 pt-2">
                  <label className="label-mini text-[#666] block" htmlFor="driver-notes">
                    Notes for driver (optional · max 500 chars)
                  </label>
                  <textarea
                    id="driver-notes"
                    data-testid="driver-notes-input"
                    rows={2}
                    maxLength={500}
                    placeholder='e.g. "1st drop: 14 Hill St café · 2nd: home"'
                    value={extras.driver_notes}
                    onChange={(e) => setExtras((s) => ({ ...s, driver_notes: e.target.value }))}
                    className="w-full border border-[#E0E0E0] p-2 text-sm focus:outline-none focus:border-[#002FA7] resize-none"
                  />
                </div>
              </div>

              {quoteLoading && (
                <div className="space-y-2" data-testid="fare-loading-skeleton">
                  <div className="border border-border bg-secondary/30 p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-[#002FA7]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Getting your fare…
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {["DISTANCE", "EST. TIME", "FARE"].map((l) => (
                        <div key={l} className="space-y-2">
                          <div className="label-mini text-muted-foreground">{l}</div>
                          <div className="h-6 w-full rounded bg-border animate-pulse" />
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1.5 pt-1">
                      <div className="h-3 w-3/4 rounded bg-border animate-pulse" />
                      <div className="h-3 w-1/2 rounded bg-border animate-pulse" />
                    </div>
                  </div>
                </div>
              )}

              {!quoteLoading && estimate && (
                <div className="space-y-2">
                  {estimate.is_fixed_route && (
                    <div className="border border-[#002FA7] bg-[#EFF3FF] p-3 flex items-center gap-3" data-testid="fixed-route-banner">
                      <div className="w-8 h-8 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-black">£</div>
                      <div className="flex-1">
                        <div className="label-mini text-[#002FA7]">FIXED FARE · {estimate.fixed_route_name?.toUpperCase()}</div>
                        <div className="text-sm font-semibold">No meter — flat-rate route price applies</div>
                      </div>
                    </div>
                  )}
                  {!estimate.is_fixed_route && estimate.surge_multiplier > 1.0 && (
                    <div className="border border-[#FFC107] bg-[#FFFBEB] p-3 flex items-center gap-3" data-testid="surge-banner">
                      <Zap className="w-5 h-5 text-[#F59E0B]" />
                      <div className="flex-1">
                        <div className="label-mini text-[#92400E]">HIGH DEMAND</div>
                        <div className="text-sm font-semibold">Fares are {estimate.surge_multiplier}× normal in this area</div>
                      </div>
                    </div>
                  )}
                  {estimate.breakdown?.late_night && (
                    <div className="border border-[#0A0A0A] bg-secondary/40 p-3 flex items-center gap-3" data-testid="latenight-banner">
                      <Moon className="w-5 h-5" />
                      <div className="flex-1">
                        <div className="label-mini">LATE-NIGHT SURCHARGE</div>
                        <div className="text-sm font-semibold">+{Math.round((estimate.breakdown.late_night_amount / Math.max(estimate.breakdown.subtotal_pre_vat_net + estimate.breakdown.vat_amount - estimate.breakdown.late_night_amount, 0.01)) * 100)}% applied · 23:00–06:00</div>
                      </div>
                    </div>
                  )}
                  <div className="border border-border bg-secondary/30 p-4 grid grid-cols-3 gap-2" data-testid="fare-estimate-card">
                    <Stat label="DISTANCE" value={`${estimate.distance_mi ?? estimate.distance_km} mi`} />
                    <Stat label="EST. TIME" value={`${Math.round(estimate.duration_min)} min`} />
                    <Stat label="FARE" value={`£${estimate.fare}`} accent="#FFC107" />
                  </div>
                  {estimate.fare_label && (
                    <p className="text-xs text-muted-foreground -mt-1" data-testid="fare-estimate-label">{estimate.fare_label}</p>
                  )}
                  {estimate.breakdown && <FareBreakdown b={estimate.breakdown} />}
                </div>
              )}

              {/* Now / Later toggle — drives whether request-ride creates an
                  immediate dispatch or a 'scheduled' ride that auto-promotes
                  ~15 min before pickup. */}
              <div data-testid="schedule-toggle">
                <Label className="label-mini text-muted-foreground mb-1.5 block">WHEN</Label>
                <div className="grid grid-cols-2 gap-1 border border-border">
                  <button
                    type="button"
                    onClick={() => { setScheduleMode("now"); setScheduledFor(""); }}
                    className={`text-sm font-bold px-3 py-2 transition-colors flex items-center justify-center gap-1.5 ${
                      scheduleMode === "now" ? "bg-[#0A0A0A] text-white" : "hover:bg-secondary"
                    }`}
                    data-testid="schedule-now-btn"
                  >
                    <Zap className="w-3.5 h-3.5" /> Now
                  </button>
                  <button
                    type="button"
                    onClick={() => { setScheduleMode("later"); setScheduledFor((v) => v || defaultScheduleValue()); }}
                    className={`text-sm font-bold px-3 py-2 transition-colors flex items-center justify-center gap-1.5 ${
                      scheduleMode === "later" ? "bg-[#0A0A0A] text-white" : "hover:bg-secondary"
                    }`}
                    data-testid="schedule-later-btn"
                  >
                    <Calendar className="w-3.5 h-3.5" /> Schedule
                  </button>
                </div>
                {scheduleMode === "later" && (
                  <div className="mt-2 space-y-1">
                    <Input
                      type="datetime-local"
                      value={scheduledFor}
                      onChange={(e) => setScheduledFor(e.target.value)}
                      min={formatLondonLocal(new Date(Date.now() + 16 * 60 * 1000))}
                      max={formatLondonLocal(new Date(Date.now() + SCHEDULE_MAX_AHEAD_MS))}
                      data-testid="schedule-datetime-input"
                    />
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      UK time · driver auto-dispatched 15 min before pickup · book up to 30 days ahead.
                    </p>
                  </div>
                )}
              </div>

              {/* CTA is gated on pickup + drop-off (+ a valid time when
                  scheduling) only. The fare `estimate` is a PASSIVE display
                  hint — the backend recomputes the fare authoritatively on
                  request-ride — so an estimate hiccup must never block booking
                  (previously `!estimate` here dead-locked the button in both
                  Now and Schedule modes when the estimate call was unavailable). */}
              <Button onClick={requestRide} disabled={requesting || bookingIssues.length > 0} className="w-full" size="lg" data-testid="request-ride-btn">
                {requesting ? (scheduleMode === "later" ? "Scheduling…" : "Searching…")
                  : scheduleMode === "later" ? "Schedule ride" : "Request taxi"}
              </Button>
              {!requesting && bookingIssues.length > 0 && (
                <ul className="text-xs text-[#B45309] space-y-1 -mt-1" data-testid="booking-missing-fields" aria-live="polite">
                  {bookingIssues.map((i) => (
                    <li key={i.id} className="flex items-start gap-1.5" data-testid={`missing-${i.id}-msg`}>
                      <span aria-hidden className="mt-1.5 w-1 h-1 rounded-full bg-current flex-shrink-0" />{i.text}
                    </li>
                  ))}
                </ul>
              )}
              {pickup && dropoff && !estimate && !quoteLoading && (
                <p className="text-xs text-muted-foreground text-center -mt-1" data-testid="estimate-unavailable-note">
                  Price unavailable right now — we couldn't plan a road route. Please try again shortly or call us.
                </p>
              )}
              </>)}
            </>
          ) : (
            <>
            {pollFailures >= 3 && (
              <div className="mb-3 border border-[#F59E0B] bg-[#FFFBEB] text-[#92400E] text-xs font-semibold px-3 py-2" data-testid="trip-connection-warning-detail">
                Connection lost — reconnecting. Trip status and fare below may be out of date.
              </div>
            )}
            <ActiveRideDetail ride={activeRide} dispatchStatus={dispatchStatus} onCancel={cancelRide} onPayCard={() => payWithCard(activeRide.id)} paying={paying} onMessage={() => setChatOpen(true)} />
            </>
          )}

          {/* Upcoming scheduled rides — shown whether or not the passenger has
              an active ride, because they're queued for the future. */}
          {scheduledRides.length > 0 && (
            <div className="border-t border-border pt-5" data-testid="scheduled-rides-section">
              <div className="label-mini text-muted-foreground mb-3 flex items-center gap-2">
                <Calendar className="w-3 h-3" /> UPCOMING SCHEDULED RIDES ({scheduledRides.length})
              </div>
              <div className="space-y-2">
                {scheduledRides.map((r) => (
                  <ScheduledRideCard key={r.id} ride={r} onCancel={() => cancelScheduledRide(r.id)} />
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-border pt-5">
            <div className="label-mini text-muted-foreground mb-3">RECENT TRIPS</div>
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="ride-history">
              {rideHistory.length === 0 && <div className="text-sm text-muted-foreground">No trips yet.</div>}
              {rideHistory.slice(0, 8).map((r) => (
                <HistoryItem key={r.id} ride={r} onPayCard={() => payWithCard(r.id)} onRate={() => { setRideToRate(r); setShowRating(true); }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <CompletionSummaryDialog
        ride={completedRide}
        onRate={() => { setRideToRate(completedRide); setShowRating(true); dismissCompletion(); }}
        onDone={() => { dismissCompletion(); fetchHistory(); }}
      />
      <RatingDialog ride={rideToRate} open={showRating} onOpenChange={setShowRating} onRated={fetchHistory} />

      {/* Ride-scoped chat — passenger participates as "passenger" role.
          Phase 1: templated quick-sends only. Auto-dismisses (and the
          backend returns []) when the ride completes/cancels. */}
      {activeRide && user && (
        <RideChatDialog
          open={chatOpen}
          onOpenChange={setChatOpen}
          rideId={activeRide.id}
          selfId={user.id}
          selfRole="passenger"
          counterpartyName={activeRide.driver?.name}
          incomingMessage={incomingMessage}
        />
      )}
    </div>
  );
}






